import { useState, useCallback, useEffect } from 'react';
import { Loader2, Clock, Package } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useStickers } from '../hooks/useStickers';
import type { StickerSetInfo, StickerInfo, SendStickerData } from '../types';

interface StickerPickerProps {
  /** Триггер элемент для открытия пикера */
  children: React.ReactNode;
  /** Callback при выборе стикера */
  onStickerSelect: (data: SendStickerData) => void;
  /** Открыт ли пикер */
  open?: boolean;
  /** Callback при изменении состояния */
  onOpenChange?: (open: boolean) => void;
  /** Disabled состояние */
  disabled?: boolean;
}

export function StickerPicker({
  children,
  onStickerSelect,
  open,
  onOpenChange,
  disabled = false,
}: StickerPickerProps) {
  const {
    stickerSets,
    recentStickers,
    currentSetStickers,
    isLoading,
    isLoadingSet,
    fetchStickerSets,
    fetchRecentStickers,
    fetchSetStickers,
    getStickerThumbUrl,
  } = useStickers();

  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'recent' | 'sets'>('recent');

  // Загружаем данные при первом открытии
  useEffect(() => {
    if (open && stickerSets.length === 0) {
      fetchStickerSets();
      fetchRecentStickers();
    }
  }, [open, stickerSets.length, fetchStickerSets, fetchRecentStickers]);

  // При выборе набора загружаем его стикеры
  const handleSelectSet = useCallback(async (set: StickerSetInfo) => {
    setSelectedSetId(set.id);
    await fetchSetStickers(set.id, set.accessHash);
  }, [fetchSetStickers]);

  // Обработка выбора стикера
  const handleSelectSticker = useCallback((sticker: StickerInfo) => {
    onStickerSelect({
      documentId: sticker.id,
      accessHash: sticker.accessHash,
      fileReference: sticker.fileReference,
    });
    onOpenChange?.(false);
  }, [onStickerSelect, onOpenChange]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-[340px] p-0"
        side="top"
        align="end"
        sideOffset={8}
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'recent' | 'sets')}>
          <div className="border-b p-2">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="recent" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                Недавние
              </TabsTrigger>
              <TabsTrigger value="sets" className="text-xs">
                <Package className="h-3 w-3 mr-1" />
                Наборы
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="recent" forceMount className="m-0 data-[state=inactive]:hidden">
            <StickerGrid
              stickers={recentStickers}
              isLoading={isLoading}
              onSelect={handleSelectSticker}
              getStickerThumbUrl={getStickerThumbUrl}
              emptyMessage="Нет недавних стикеров"
            />
          </TabsContent>

          <TabsContent value="sets" forceMount className="m-0 data-[state=inactive]:hidden">
            {selectedSetId ? (
              <div>
                {/* Кнопка назад к наборам */}
                <button
                  onClick={() => setSelectedSetId(null)}
                  className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted flex items-center gap-2 border-b"
                >
                  ← Назад к наборам
                </button>
                <StickerGrid
                  stickers={currentSetStickers}
                  isLoading={isLoadingSet}
                  onSelect={handleSelectSticker}
                  getStickerThumbUrl={getStickerThumbUrl}
                  emptyMessage="В наборе нет стикеров"
                />
              </div>
            ) : (
              <StickerSetList
                sets={stickerSets}
                isLoading={isLoading}
                onSelectSet={handleSelectSet}
              />
            )}
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}

interface StickerGridProps {
  stickers: StickerInfo[];
  isLoading: boolean;
  onSelect: (sticker: StickerInfo) => void;
  getStickerThumbUrl: (sticker: StickerInfo) => string;
  emptyMessage: string;
}

function StickerGrid({ stickers, isLoading, onSelect, getStickerThumbUrl, emptyMessage }: StickerGridProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (stickers.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <ScrollArea className="h-[280px]">
      <div className="grid grid-cols-5 gap-1 p-2">
        {stickers.map((sticker) => (
          <button
            key={sticker.id}
            onClick={() => onSelect(sticker)}
            className={cn(
              'aspect-square rounded-lg overflow-hidden',
              'hover:bg-muted transition-colors',
              'flex items-center justify-center p-1'
            )}
            title={sticker.emoji}
          >
            {sticker.isAnimated || sticker.isVideo ? (
              // Для анимированных и видео стикеров показываем эмодзи как fallback
              <span className="text-2xl">{sticker.emoji}</span>
            ) : (
              <img
                src={getStickerThumbUrl(sticker)}
                alt={sticker.emoji}
                className="max-w-full max-h-full object-contain"
                loading="lazy"
              />
            )}
          </button>
        ))}
      </div>
    </ScrollArea>
  );
}

interface StickerSetListProps {
  sets: StickerSetInfo[];
  isLoading: boolean;
  onSelectSet: (set: StickerSetInfo) => void;
}

function StickerSetList({ sets, isLoading, onSelectSet }: StickerSetListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (sets.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
        У вас нет сохранённых наборов стикеров
      </div>
    );
  }

  return (
    <ScrollArea className="h-[280px]">
      <div className="p-2 space-y-1">
        {sets.map((set) => (
          <button
            key={set.id}
            onClick={() => onSelectSet(set)}
            className={cn(
              'w-full px-3 py-2 rounded-lg',
              'hover:bg-muted transition-colors',
              'flex items-center justify-between gap-2 text-left'
            )}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{set.title}</p>
              <p className="text-xs text-muted-foreground">
                {set.count} стикеров
                {set.isAnimated && ' • Анимированные'}
                {set.isVideo && ' • Видео'}
              </p>
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
}
